import { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, Modal, Pressable, TextInput } from 'react-native';
import { usePostHog } from 'posthog-js/react';
import styled from 'styled-components/native';
import { REDESIGN_COLORS } from '../../styles/colors';
import supabase from '../../utils/supabaseClient';
import { apiService } from '../../services/apiService';
import logger from '../../utils/logger';
import { useAuth } from '../../context/AuthContext';
import { useTier } from '../../context/TierContext';
import { handleProAccessError } from '../../utils/handleProAccessError';
import Skeleton from './detail/Skeleton';

// --- Styled Components ---

const Card = styled.View`
  padding: 16px;
  background-color: ${REDESIGN_COLORS.cardBg};
  border-radius: 12px;
  border-width: 1px;
  border-color: ${REDESIGN_COLORS.borderSubtle};
`;

const Title = styled.Text`
  font-family: 'Satoshi-Bold';
  font-size: 15px;
  color: ${REDESIGN_COLORS.textPrimary};
  margin-bottom: 8px;
`;

const Description = styled.Text`
  font-family: 'Satoshi-Regular';
  font-size: 13px;
  line-height: 20px;
  color: ${REDESIGN_COLORS.textSecondary};
  margin-bottom: 16px;
`;

const HandoffButton = styled(Pressable)`
  align-self: flex-start;
  background-color: ${REDESIGN_COLORS.ctaBlue};
  padding: 10px 18px;
  border-radius: 8px;
  align-items: center;
  justify-content: center;
`;

const HandoffButtonText = styled.Text`
  font-family: 'Satoshi-Bold';
  font-size: 14px;
  color: ${REDESIGN_COLORS.textPrimary};
`;

const Overlay = styled.View`
  flex: 1;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.6);
`;

const ModalCard = styled.View`
  width: 90%;
  max-width: 400px;
  background-color: ${REDESIGN_COLORS.cardBg};
  border-radius: 16px;
  padding: 24px;
  border-width: 1px;
  border-color: ${REDESIGN_COLORS.borderSubtle};
`;

const ModalTitle = styled.Text`
  font-family: 'Satoshi-Bold';
  font-size: 18px;
  color: ${REDESIGN_COLORS.textPrimary};
  margin-bottom: 8px;
`;

const ModalDescription = styled.Text`
  font-family: 'Satoshi-Regular';
  font-size: 13px;
  line-height: 20px;
  color: ${REDESIGN_COLORS.textSecondary};
  margin-bottom: 20px;
`;

const FieldLabel = styled.Text`
  font-family: 'Satoshi-Medium';
  font-size: 13px;
  color: ${REDESIGN_COLORS.textSecondary};
  margin-bottom: 6px;
  margin-top: 12px;
`;

const InputField = styled.View`
  border-width: 1px;
  border-color: ${REDESIGN_COLORS.borderSubtle};
  border-radius: 8px;
  background-color: rgba(255, 255, 255, 0.05);
`;

const ButtonRow = styled.View`
  flex-direction: row;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
`;

const CancelButton = styled(Pressable)`
  padding: 12px 20px;
  border-radius: 8px;
`;

const CancelButtonText = styled.Text`
  font-family: 'Satoshi-Medium';
  font-size: 14px;
  color: ${REDESIGN_COLORS.textSecondary};
`;

const ConfirmButton = styled(Pressable)`
  background-color: ${REDESIGN_COLORS.ctaBlue};
  padding: 12px 20px;
  border-radius: 8px;
  align-items: center;
  justify-content: center;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
`;

const ConfirmButtonText = styled.Text`
  font-family: 'Satoshi-Bold';
  font-size: 14px;
  color: ${REDESIGN_COLORS.textPrimary};
`;

const ErrorText = styled.Text`
  font-family: 'Satoshi-Regular';
  font-size: 13px;
  color: #ef4444;
  margin-top: 8px;
`;

const DealerInfoContainer = styled.View`
  margin-top: 16px;
  padding: 12px;
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  border-width: 1px;
  border-color: ${REDESIGN_COLORS.borderSubtle};
`;

const DealerInfoLabel = styled.Text`
  font-family: 'Satoshi-Bold';
  font-size: 13px;
  color: ${REDESIGN_COLORS.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`;

const DealerInfoText = styled.Text`
  font-family: 'Satoshi-Medium';
  font-size: 14px;
  color: ${REDESIGN_COLORS.textPrimary};
  line-height: 22px;
`;

const inputStyle = {
  fontFamily: 'Satoshi-Medium',
  fontSize: 14,
  color: REDESIGN_COLORS.textPrimary,
  paddingVertical: 12,
  paddingHorizontal: 12,
  outlineStyle: 'none',
};

const SummaryBlock = styled.View`
  margin-top: 4px;
  margin-bottom: 16px;
  padding: 12px 14px;
  border-radius: 10px;
  border-width: 1px;
  border-color: ${REDESIGN_COLORS.borderSubtle};
  background-color: rgba(255, 255, 255, 0.04);
  gap: 14px;
`;

const SummarySection = styled.View`
  gap: 6px;
`;

const SummaryLabel = styled.Text`
  font-family: 'Satoshi-Bold';
  font-size: 11px;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: ${REDESIGN_COLORS.textSecondary};
`;

const SummaryItem = styled.Text`
  font-family: 'Satoshi-Regular';
  font-size: 13px;
  line-height: 19px;
  color: ${REDESIGN_COLORS.textPrimary};
`;

const SummaryHint = styled.Text`
  font-family: 'Satoshi-Regular';
  font-size: 12px;
  line-height: 17px;
  color: ${REDESIGN_COLORS.textSecondary};
`;

/**
 * HandoffCard - Reveals identity and hands off negotiation to the user.
 *
 * Displays a "Reveal your identity" button. On tap, opens a confirmation
 * modal with editable fields for email, phone (optional), and name.
 *
 * On confirm:
 * 1. INSERT negotiation_messages row with handoff content
 * 2. Call apiService for send_approved_draft with is_handoff: true
 *
 * After handoff: display dealer contact info from listing snapshot.
 *
 * @param {Object} props
 * @param {Object} props.negotiation - The negotiation object
 * @param {Function} props.onHandoff - Callback after successful handoff
 */
const HandoffCard = ({ negotiation, onHandoff }) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // Dealer-address lookup state. `lookup` is one of
  // 'idle' | 'loading' | 'success' | 'error'. `dealer` holds the merged
  // contact payload (snapshot fallback fields + edge-fn enrichment).
  const [lookup, setLookup] = useState('idle');
  const [dealer, setDealer] = useState(null);
  const { session } = useAuth();
  const { triggerUpgradeModal } = useTier();
  const posthog = usePostHog();

  const isHandedOff = negotiation?.status === 'handed_off';
  const listingSnapshot = negotiation?.listing_snapshot_at_start || {};
  // Snapshot-derived fallback contact bag. These render immediately on the
  // handed_off branch so the lookup never blocks the basic name/phone/email
  // lines. The address fields (street, postal_code, country) are not in the
  // snapshot today so they only appear once the edge function returns.
  const snapshotDealer = {
    name: listingSnapshot.dealer_name || null,
    phone: listingSnapshot.dealer_phone || null,
    email: negotiation?.dealer_email || listingSnapshot.dealer_email || null,
    street: listingSnapshot.street || null,
    city: listingSnapshot.city || null,
    postal_code: listingSnapshot.postal_code || null,
    country: listingSnapshot.country || null,
  };

  // Fire one server-side dealer-address lookup whenever this card lands on the
  // handed_off branch. Result merges over the snapshot-derived bag.
  //
  // StrictMode note (per spec task 26): in dev React mounts effects twice. We
  // intentionally do NOT guard with `if (lookup !== 'idle') return;`, that
  // would block re-fetches after a real status flip in production. The dev
  // duplicate is acceptable; the function is read-only and idempotent.
  useEffect(() => {
    if (negotiation?.status !== 'handed_off' || !negotiation?.id) {
      return;
    }
    let cancelled = false;
    setLookup('loading');
    apiService
      .invokeEdgeFunction('get-handoff-dealer-address', {
        body: { negotiation_id: negotiation.id },
      })
      .then((payload) => {
        if (cancelled) return;
        if (payload && typeof payload === 'object' && Object.keys(payload).length > 0) {
          setDealer(payload);
          setLookup('success');
        } else {
          // Empty payload, e.g. listing_cache row missing or status mismatch.
          // Treat the same as an error: snapshot fallback.
          setDealer(null);
          setLookup('error');
        }
      })
      .catch((err) => {
        if (cancelled) return;
        logger.error('[HandoffCard] dealer address lookup failed:', err);
        setDealer(null);
        setLookup('error');
      });
    return () => {
      cancelled = true;
    };
  }, [negotiation?.status, negotiation?.id]);

  // Merged contact bag rendered on the handed_off branch. Edge-function
  // results win over snapshot when present; otherwise the snapshot value
  // shows. Each field can be null.
  const mergedDealer = {
    name: dealer?.name || snapshotDealer.name,
    phone: dealer?.phone || snapshotDealer.phone,
    email: dealer?.email || snapshotDealer.email,
    street: dealer?.street || snapshotDealer.street,
    city: dealer?.city || snapshotDealer.city,
    postal_code: dealer?.postal_code || snapshotDealer.postal_code,
    country: dealer?.country || snapshotDealer.country,
  };

  const handleOpenConfirm = useCallback(() => {
    // Prefill chain: negotiation setup_config takes priority (most recent
    // intent), then auth session (email + display name from user_metadata).
    // Phone has no auth source, so it stays optional and blank unless the
    // user provided one in setup.
    const setupConfig = negotiation?.setup_config || {};
    const authUser = session?.user;
    setEmail(setupConfig.user_email || authUser?.email || '');
    setPhone(setupConfig.user_phone || '');
    setName(
      setupConfig.user_name
        || authUser?.user_metadata?.full_name
        || authUser?.user_metadata?.name
        || ''
    );
    setShowConfirm(true);
    setError(null);
  }, [negotiation, session]);

  const handleConfirm = useCallback(async () => {
    if (!email.trim()) {
      setError('Please enter your email address.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Build the handoff email body. Assertive header so the dealer
      // clearly understands this is the buyer taking over from the
      // DooCars-managed thread, then a clean section split for the deal
      // reference and the buyer's direct contact details.
      const snap = negotiation?.listing_snapshot_at_start || {};
      const vehicleParts = [snap.year, snap.make, snap.model].filter(Boolean);
      const vehicle = vehicleParts.join(' ');
      const vrm = snap.vehicle_registration_mark || snap.vrm || null;
      const vehicleLine = vehicle
        ? (vrm ? `${vehicle} (${vrm})` : vehicle)
        : 'this vehicle';
      const agreedPrice = negotiation?.current_dealer_offer || negotiation?.current_buyer_offer;
      const refId = negotiation?.id?.substring(0, 8)?.toUpperCase() || '';
      const buyerName = name.trim() || null;
      const divider = '\u2500'.repeat(40);

      const handoffContent = [
        divider,
        `BUYER NOW IN DIRECT CONTACT`,
        divider,
        ``,
        `Hi,`,
        ``,
        `Good news, the buyer is happy to take this one over directly. The DooCars-assisted thread is now closed. Please reply to ${buyerName ? buyerName : 'them'} on the contact below, replies to this address will not reach the buyer.`,
        ``,
        `DEAL REFERENCE`,
        `  Vehicle: ${vehicleLine}`,
        agreedPrice ? `  Agreed price: \u00a3${Number(agreedPrice).toLocaleString('en-GB')}` : null,
        refId ? `  Reference: DC-${refId}` : null,
        ``,
        `BUYER'S DIRECT CONTACT`,
        buyerName ? `  Name:  ${buyerName}` : null,
        `  Email: ${email.trim()}`,
        phone.trim() ? `  Phone: ${phone.trim()}` : null,
        ``,
        `Thanks for working with us, and best of luck closing the sale.`,
        ``,
        `DooCars`,
      ]
        .filter(Boolean)
        .join('\n');

      // Step 1: INSERT message row. Handoff metadata goes into
      // structured_input (the schema has no `metadata` column on
      // negotiation_messages, structured_input is the JSONB scratch field).
      const { data: messageData, error: insertError } = await supabase
        .from('negotiation_messages')
        .insert({
          negotiation_id: negotiation.id,
          user_id: session?.user?.id,
          role: 'user_written',
          content: handoffContent,
          status: 'approved',
          structured_input: {
            handoff: {
              contact_email: email.trim(),
              contact_phone: phone.trim() || null,
              contact_name: name.trim() || null,
            },
          },
        })
        .select('id')
        .single();

      if (insertError) throw insertError;

      // Step 2: Call edge function to send + update status to handed_off
      await apiService.requestNegotiationAction('send_approved_draft', {
        negotiation_id: negotiation.id,
        message_id: messageData.id,
        is_handoff: true,
      });

      posthog?.capture('handoff_revealed', {
        negotiation_id: negotiation.id,
        provided_phone: !!phone.trim(),
        provided_name: !!name.trim(),
      });

      setShowConfirm(false);

      if (onHandoff) {
        onHandoff();
      }
    } catch (err) {
      if (handleProAccessError(err, triggerUpgradeModal, 'negotiation_handoff')) {
        setShowConfirm(false);
        setSubmitting(false);
        return;
      }
      logger.error('[HandoffCard] Handoff failed:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [negotiation, email, phone, name, onHandoff, triggerUpgradeModal, posthog]);

  // ----- handed_off variant: read-only contact card -----
  // Name / phone / email render immediately from snapshot. The address row
  // (street, city, postal_code, country) shows a Skeleton while the lookup
  // is in flight; on success it shows the live address; on error/empty it
  // falls back to whatever address fields the snapshot carries.
  if (isHandedOff) {
    const hasAnyContact = !!(
      mergedDealer.name
      || mergedDealer.phone
      || mergedDealer.email
      || mergedDealer.street
      || mergedDealer.city
      || mergedDealer.postal_code
      || mergedDealer.country
    );

    return (
      <Card>
        <Title>You're now in direct contact</Title>
        <Description>
          We've sent your details to the dealer. From here, you can reach them directly using the contact below.
        </Description>
        {hasAnyContact && (
          <DealerInfoContainer>
            <DealerInfoLabel>Dealer contact</DealerInfoLabel>
            {mergedDealer.name && (
              <DealerInfoText>{mergedDealer.name}</DealerInfoText>
            )}
            {mergedDealer.phone && (
              <DealerInfoText>{mergedDealer.phone}</DealerInfoText>
            )}
            {mergedDealer.email && (
              <DealerInfoText>{mergedDealer.email}</DealerInfoText>
            )}
            {lookup === 'loading' ? (
              <Skeleton variant="text" count={2} ariaLabel="Loading dealer address" />
            ) : (
              <>
                {mergedDealer.street && (
                  <DealerInfoText>{mergedDealer.street}</DealerInfoText>
                )}
                {mergedDealer.city && (
                  <DealerInfoText>{mergedDealer.city}</DealerInfoText>
                )}
                {mergedDealer.postal_code && (
                  <DealerInfoText>{mergedDealer.postal_code}</DealerInfoText>
                )}
                {mergedDealer.country && (
                  <DealerInfoText>{mergedDealer.country}</DealerInfoText>
                )}
              </>
            )}
          </DealerInfoContainer>
        )}
      </Card>
    );
  }

  // Build the handoff preview so the user can see what's about to change hands.
  const previewVehicle = [listingSnapshot.year, listingSnapshot.make, listingSnapshot.model]
    .filter(Boolean)
    .join(' ') || 'this vehicle';
  const previewVrm = listingSnapshot.vehicle_registration_mark || null;
  const agreedPrice = negotiation?.current_dealer_offer || negotiation?.current_buyer_offer || null;
  const agreedPriceLabel = agreedPrice
    ? `\u00a3${Number(agreedPrice).toLocaleString('en-GB')}`
    : null;
  const refIdLabel = negotiation?.id
    ? `DC-${negotiation.id.substring(0, 8).toUpperCase()}`
    : null;

  return (
    <>
      <Card>
        <Title>Take it from here</Title>
        <Description>
          Continue the conversation directly with the dealer. Here's exactly what changes hands.
        </Description>

        <SummaryBlock>
          <SummarySection>
            <SummaryLabel>You'll send the dealer</SummaryLabel>
            <SummaryItem>
              {`\u2022 Your contact details (email, phone and name optional)`}
            </SummaryItem>
            <SummaryItem>
              {`\u2022 Vehicle: ${previewVehicle}${previewVrm ? ` (${previewVrm})` : ''}`}
            </SummaryItem>
            {agreedPriceLabel && (
              <SummaryItem>{`\u2022 Agreed price: ${agreedPriceLabel}`}</SummaryItem>
            )}
            {refIdLabel && (
              <SummaryItem>{`\u2022 Reference: ${refIdLabel}`}</SummaryItem>
            )}
          </SummarySection>

          <SummarySection>
            <SummaryLabel>You'll get back</SummaryLabel>
            {snapshotDealer.name && (
              <SummaryItem>{`\u2022 Dealer name: ${snapshotDealer.name}`}</SummaryItem>
            )}
            {snapshotDealer.phone && (
              <SummaryItem>{`\u2022 Dealer phone: ${snapshotDealer.phone}`}</SummaryItem>
            )}
            {snapshotDealer.city && (
              <SummaryItem>{`\u2022 Location: ${snapshotDealer.city}`}</SummaryItem>
            )}
            <SummaryItem>{`\u2022 The full conversation stays in your DooCars timeline`}</SummaryItem>
          </SummarySection>

          <SummaryHint>
            You can review and edit your details on the next step before anything is sent.
          </SummaryHint>
        </SummaryBlock>

        <HandoffButton onPress={handleOpenConfirm}>
          <HandoffButtonText>Share my details</HandoffButtonText>
        </HandoffButton>
      </Card>

      <Modal
        animationType="fade"
        transparent
        visible={showConfirm}
        onRequestClose={() => setShowConfirm(false)}
      >
        <Overlay>
          <ModalCard>
            <ModalTitle>Share your details?</ModalTitle>
            <ModalDescription>
              The dealer will see these as soon as you send. After this, you talk to them directly and we step out.
            </ModalDescription>

            <FieldLabel>Email *</FieldLabel>
            <InputField>
              <TextInput
                style={inputStyle}
                value={email}
                onChangeText={setEmail}
                placeholder="your@email.com"
                placeholderTextColor={REDESIGN_COLORS.textSecondary}
                keyboardType="email-address"
                autoCapitalize="none"
                editable={!submitting}
              />
            </InputField>

            <FieldLabel>Phone (optional)</FieldLabel>
            <InputField>
              <TextInput
                style={inputStyle}
                value={phone}
                onChangeText={setPhone}
                placeholder="07700 900000"
                placeholderTextColor={REDESIGN_COLORS.textSecondary}
                keyboardType="phone-pad"
                autoComplete="tel"
                inputMode="tel"
                editable={!submitting}
              />
            </InputField>

            <FieldLabel>Name (optional)</FieldLabel>
            <InputField>
              <TextInput
                style={inputStyle}
                value={name}
                onChangeText={setName}
                placeholder="Your name"
                placeholderTextColor={REDESIGN_COLORS.textSecondary}
                editable={!submitting}
              />
            </InputField>

            {error && <ErrorText>{error}</ErrorText>}

            <ButtonRow>
              <CancelButton onPress={() => setShowConfirm(false)} disabled={submitting}>
                <CancelButtonText>Cancel</CancelButtonText>
              </CancelButton>
              <ConfirmButton onPress={handleConfirm} disabled={submitting}>
                {submitting ? (
                  <ActivityIndicator size="small" color={REDESIGN_COLORS.textPrimary} />
                ) : (
                  <ConfirmButtonText>Send</ConfirmButtonText>
                )}
              </ConfirmButton>
            </ButtonRow>
          </ModalCard>
        </Overlay>
      </Modal>
    </>
  );
};

export default HandoffCard;
